/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { IEditor } from "../../IEditor";
import { termsEditorViewEditor } from "../../terms/viewEditor";
import { TOAST_TYPE } from "../../toasts/IToast";
import { ViewEditorView } from "./ViewEditorEdit/ViewEditorView";
import { LayoutState_Panel } from "../../ui/components/Layout/LayoutState";
import { CreateViewDialog } from "./CreateViewDialog/CreateViewDialog";
import { CMD } from "../../commands/CMD";
import { ErrorTracker } from "@adapptio/lib-components";

export function registerCommands(dc: IEditor): void {
	const viewSvc = dc.get("viewService");
	const commandsSvc = dc.get("commandsService");
	const resourceSvc = dc.get("resourceService");
	const dialogSvc = dc.get("dialogService");
	const toastSvc = dc.get("toastService");

	commandsSvc.register({
		id: CMD.VIEW_EDITOR.CREATE.OPEN,
		label: termsEditorViewEditor.mainMenu.sectionCreate.view.label,
		args: [
			{
				label: "Base path",
				isOptional: true,
				schema: {
					type: "string"
				}
			}
		],
		handler: async (args) => {
			const basePath = args[0] as string;

			const res = await dialogSvc.open(CreateViewDialog, "viewEditor.createView", { basePath });

			if (res.resourceId) {
				commandsSvc.execute("resource.open", res.resourceId);
			}

			return res.resourceId;
		}
	});

	commandsSvc.register({
		id: CMD.VIEW_EDITOR.EDIT.UNDO,
		label: termsEditorViewEditor.commands.undo,
		handler: async () => {
			const viewEditor = viewSvc.getCurrentView() as ViewEditorView;
			viewEditor.undo();
		}
	});

	commandsSvc.register({
		id: CMD.VIEW_EDITOR.EDIT.REDO,
		label: termsEditorViewEditor.commands.redo,
		handler: async () => {
			const viewEditor = viewSvc.getCurrentView() as ViewEditorView;
			viewEditor.redo();
		}
	});

	commandsSvc.register({
		id: CMD.VIEW_EDITOR.EDIT.TOGGLE_LEFT_PANEL,
		label: termsEditorViewEditor.commands.toggleLeftPanel,
		handler: async () => {
			if (viewSvc.isViewActive("viewEditor.edit")) {
				(
					(viewSvc.getCurrentView() as ViewEditorView)
						.getLayout()
						.getById("panelLeft") as LayoutState_Panel
				)?.toggleExpanded();
			}
		}
	});

	commandsSvc.register({
		id: CMD.VIEW_EDITOR.EDIT.TOGGLE_RIGHT_PANEL,
		label: termsEditorViewEditor.commands.toggleRightPanel,
		handler: async () => {
			if (viewSvc.isViewActive("viewEditor.edit")) {
				const currentView = viewSvc.getCurrentView();

				if (currentView instanceof ViewEditorView) {
					const layout = currentView.getLayout();
					const panelRight = layout.getById("panelRight") as LayoutState_Panel;

					if (panelRight) {
						panelRight.toggleExpanded();
					}
				}
			}
		}
	});

	commandsSvc.register({
		id: CMD.VIEW_EDITOR.EDIT.HIDE_ALL_PANELS,
		label: termsEditorViewEditor.commands.hideAllPanels,
		handler: async () => {
			if (viewSvc.isViewActive("viewEditor.edit")) {
				const layout = (viewSvc.getCurrentView() as ViewEditorView).getLayout();

				(layout.getById("panelLeft") as LayoutState_Panel)?.collapse();
				(layout.getById("panelRight") as LayoutState_Panel)?.collapse();
			}
		}
	});

	commandsSvc.register({
		id: CMD.VIEW_EDITOR.EDIT.TOGGLE_CONSOLE,
		label: termsEditorViewEditor.commands.toggleConsole,
		handler: async () => {
			if (viewSvc.isViewActive("viewEditor.edit")) {
				(
					(viewSvc.getCurrentView() as ViewEditorView)
						.getLayout()
						.getById("console") as LayoutState_Panel
				)?.toggleExpanded();
			}
		}
	});

	commandsSvc.register({
		id: CMD.VIEW_EDITOR.EDIT.SAVE_RESOURCE,
		label: termsEditorViewEditor.commands.saveResource,
		handler: async () => {
			if (viewSvc.isViewActive("viewEditor.edit")) {
				const viewEditor = viewSvc.getCurrentView() as ViewEditorView;

				if (!viewEditor.getIsModified() || viewEditor.isBeingSaved()) {
					return;
				}

				const loadingToast = toastSvc.add({
					type: TOAST_TYPE.LOADING,
					message: termsEditorViewEditor.toasts.savingView,
					isClosable: false
				});

				try {
					await viewEditor.save();

					toastSvc.add({
						type: TOAST_TYPE.SUCCESS,
						message: termsEditorViewEditor.toasts.viewHasBeenSaved,
						timeoutMs: 2500
					});
				} catch (err) {
					const errorTracker = ErrorTracker.getInstance();
					errorTracker.captureException(err);

					toastSvc.add({
						type: TOAST_TYPE.ERROR,
						message: termsEditorViewEditor.toasts.failedToSaveView,
						details: String(err)
					});
				} finally {
					toastSvc.remove(loadingToast);
				}
			}
		}
	});

	commandsSvc.register({
		id: CMD.VIEW_EDITOR.EDIT.OPEN_HISTORY,
		label: termsEditorViewEditor.other.historyNotAvailableYet,
		handler: async () => {
			toastSvc.add({
				type: TOAST_TYPE.INFO,
				message: "History is not implemented yet."
			});
		}
	});

	commandsSvc.register({
		id: CMD.VIEW_EDITOR.EDIT.DELETE_SELECTION,
		label: termsEditorViewEditor.commands.deleteSelection,
		handler: async () => {
			if (viewSvc.isViewActive("viewEditor.edit")) {
				const viewEditor = viewSvc.getCurrentView() as ViewEditorView;
				viewEditor.getViewModel().deleteSelection();
			}
		}
	});

	commandsSvc.register({
		id: CMD.VIEW_EDITOR.EDIT.UNSELECT_ALL,
		label: termsEditorViewEditor.commands.unselectAll,
		handler: async () => {
			if (viewSvc.isViewActive("viewEditor.edit")) {
				const viewEditor = viewSvc.getCurrentView() as ViewEditorView;
				viewEditor.getViewModel().getSelection().clear();
			}
		}
	});

	commandsSvc.register({
		id: CMD.VIEW_EDITOR.EDIT.OPEN_MAIN_VIEW,
		label: termsEditorViewEditor.commands.openMainView,
		handler: async () => {
			const mainView =
				resourceSvc
					.getResourceList()
					.find((res) => res.descriptor.name.toLowerCase() === "mainview") ||
				resourceSvc.getResourceById("main");

			if (mainView) {
				commandsSvc.execute("resource.open", mainView.id);
			}
		}
	});
}
